import React, { useState } from 'react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Settings, Edit2, School, Key, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { SettingsSection } from '../../../components/ui/SettingsSection';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { useAppNavigation } from '../../../app/hooks/useAppNavigation';
import { type Classroom, type TeacherAccount, verifyPassword, hashPassword, isValidPassword } from '../../../accounts';
interface SettingsPageProps {
  activeAccount: TeacherAccount;
  classrooms?: Classroom[];
}
export function SettingsPage({
  activeAccount,
  classrooms = []
}: SettingsPageProps) {
  // Singleton store — all consumers share the same state (Zustand refactor s81)
  const {
    accounts,
    clearActiveAccount,
    updateAccountNameById,
    updateAccountEmail,
    updateTeacherDetails,
    updatePassword
  } = useAppStore();
  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editName, setEditName] = useState(activeAccount?.displayName || '');
  const [editEmail, setEditEmail] = useState(activeAccount?.email || '');
  const [editSchool, setEditSchool] = useState(activeAccount?.schoolName || '');
  // Password change state
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const nav = useAppNavigation({
    accounts,
    activeAccount,
    isInitialized: true,
    switchAccount: () => {},
    setViewingLearner: () => {},
    loadDemoAccounts: () => {},
    clearActiveAccount
  });
  const handleSaveProfile = () => {
    if (editName.trim()) {
      updateAccountNameById(activeAccount.id, editName.trim());
    }
    if (editEmail.trim() !== (activeAccount.email || '')) {
      updateAccountEmail(activeAccount.id, editEmail.trim());
    }
    if (editSchool.trim() !== (activeAccount.schoolName || '')) {
      updateTeacherDetails(activeAccount.id, {
        schoolName: editSchool.trim()
      });
    }
    setIsEditingProfile(false);
  };
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Vul alle velden in.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('De nieuwe wachtwoorden komen niet overeen.');
      return;
    }
    if (!isValidPassword(newPassword, 'teacher')) {
      setPasswordError('Het nieuwe wachtwoord moet minimaal 6 tekens lang zijn.');
      return;
    }
    if (!activeAccount.passwordHash) {
      setPasswordError('Dit account heeft geen wachtwoord ingesteld.');
      return;
    }
    const isCorrect = await verifyPassword(currentPassword, activeAccount.passwordHash);
    if (!isCorrect) {
      setPasswordError('Het huidige wachtwoord is onjuist.');
      return;
    }
    const newHash = await hashPassword(newPassword);
    updatePassword(activeAccount.id, newHash);
    setPasswordSuccess('Je wachtwoord is succesvol gewijzigd.');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setTimeout(() => {
      setIsChangingPassword(false);
      setPasswordSuccess('');
    }, 3000);
  };
  return <PageLayout title="Instellingen" description="Beheer je docentaccount en voorkeuren" data-id="element-5850">
      <div className="max-w-3xl space-y-6" data-id="element-5851">
        {/* Account Info */}
        <SettingsSection title="Account Instellingen" description="Beheer je persoonlijke gegevens" icon={Settings} accentColor="bg-violet-100 text-violet-600" action={!isEditingProfile ? <Button onClick={() => {
        setEditName(activeAccount?.displayName || '');
        setEditEmail(activeAccount?.email || '');
        setEditSchool(activeAccount?.schoolName || '');
        setIsEditingProfile(true);
      }} variant="ghost" size="sm" data-id="element-5853">
                <Edit2 className="w-4 h-4 mr-2" data-id="element-5854" /> Bewerk
              </Button> : null} data-id="element-5852">
          <div className="space-y-6" data-id="element-5855">
            {isEditingProfile ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-5856">
                <div data-id="element-5857">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-5858">
                    Naam
                  </label>
                  <Input variant="admin" value={editName} onChange={e => setEditName(e.target.value)} placeholder="Jouw naam" autoFocus data-id="element-5859" />
                </div>
                <div data-id="element-5860">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-5861">
                    E-mailadres
                  </label>
                  <Input variant="admin" type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} placeholder="jouw@email.nl" data-id="element-5862" />
                </div>
                <div className="sm:col-span-2" data-id="element-5863">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-5864">
                    School
                  </label>
                  <Input variant="admin" value={editSchool} onChange={e => setEditSchool(e.target.value)} placeholder="Naam van je school" data-id="element-5865" />
                </div>
                <div className="sm:col-span-2 flex justify-end gap-2 mt-2" data-id="element-5866">
                  <Button onClick={() => setIsEditingProfile(false)} variant="secondary" data-id="element-5867">
                    Annuleren
                  </Button>
                  <button onClick={handleSaveProfile} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-5868">
                    Opslaan
                  </button>
                </div>
              </div> : <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-5869">
                  <div data-id="element-5870">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-5871">
                      Naam
                    </label>
                    <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium" data-id="element-5872">
                      {activeAccount?.displayName}
                    </div>
                  </div>
                  <div data-id="element-5873">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-5874">
                      E-mailadres
                    </label>
                    <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium" data-id="element-5875">
                      {activeAccount?.email || <span className="text-slate-400 italic" data-id="element-5876">
                          Niet ingesteld
                        </span>}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100" data-id="element-5877">
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100 max-w-sm" data-id="element-5878">
                    <div className="p-2 bg-white rounded-lg border border-slate-200 text-slate-500 shadow-sm" data-id="element-5879">
                      <School className="w-5 h-5" data-id="element-5880" />
                    </div>
                    <div data-id="element-5881">
                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-5882">
                        School
                      </div>
                      <div className="font-medium text-slate-900" data-id="element-5883">
                        {activeAccount?.schoolName || 'Niet opgegeven'}
                      </div>
                    </div>
                  </div>
                </div>
              </>}
          </div>
        </SettingsSection>

        {/* Password Change */}
        <SettingsSection title="Wachtwoord" description="Wijzig je wachtwoord" icon={Key} accentColor="bg-violet-100 text-violet-600" action={!isChangingPassword ? <Button onClick={() => setIsChangingPassword(true)} variant="ghost" size="sm" data-id="element-5885">
                <Edit2 className="w-4 h-4 mr-2" data-id="element-5886" /> Wijzig
              </Button> : null} data-id="element-5884">
          {isChangingPassword ? <form onSubmit={handleChangePassword} className="space-y-4" data-id="element-5887">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-5888">
                <div className="sm:col-span-2" data-id="element-5889">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-5890">
                    Huidig wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="••••••••" data-id="element-5891" />
                </div>
                <div data-id="element-5892">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-5893">
                    Nieuw wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" data-id="element-5894" />
                </div>
                <div data-id="element-5895">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-5896">
                    Bevestig nieuw wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" data-id="element-5897" />
                </div>
              </div>

              {passwordError && <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 p-3 rounded-lg border border-rose-100" data-id="element-5898">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5899" />
                  <p data-id="element-5900">{passwordError}</p>
                </div>}

              {passwordSuccess && <div className="flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 p-3 rounded-lg border border-emerald-100" data-id="element-5901">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5902" />
                  <p data-id="element-5903">{passwordSuccess}</p>
                </div>}

              <div className="flex justify-end gap-2 pt-2" data-id="element-5904">
                <Button type="button" onClick={() => {
              setIsChangingPassword(false);
              setPasswordError('');
              setPasswordSuccess('');
              setCurrentPassword('');
              setNewPassword('');
              setConfirmPassword('');
            }} variant="secondary" data-id="element-5905">
                  Annuleren
                </Button>
                <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-5906">
                  Wachtwoord Wijzigen
                </button>
              </div>
            </form> : <div className="text-sm text-slate-500" data-id="element-5907">
              Je wachtwoord is veilig opgeslagen. Klik op 'Wijzig' om een nieuw
              wachtwoord in te stellen.
            </div>}
        </SettingsSection>
      </div>
    </PageLayout>;
}